import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * AuthContext — Global Brain for User + Branch State
 *
 * localStorage key: 'csm_session'
 * Stored shape: {
 *   user:           { id, name, email, role, branch_id }
 *   tenant:         { id, company_name, plan_name, max_branches }
 *   allBranches:    [ { id, name, is_main, ... } ]
 *   activeBranch:   { id, name, is_main } | null (null = "All Branches" aggregate view)
 *   userType:       'company_admin' | 'branch_admin' | 'branch_user'
 * }
 */

const AuthContext = createContext(null);

const SESSION_KEY = 'csm_session';
const ROLE_KEY    = 'csm_role';
const USER_KEY    = 'csm_user'; // kept for backward-compat with ProtectedRoute

// ─── Helpers ────────────────────────────────────────────────────────────────

export const readSession = () => {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const writeSession = (data) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
    // Keep legacy keys so ProtectedRoute & MainLayout still work during transition
    if (data.user)  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    if (data.role)  localStorage.setItem(ROLE_KEY, data.role);
};

export const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem('csm_token');
};

// ─── Provider ────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(() => readSession());

    // Sync state ↔ localStorage whenever session changes
    useEffect(() => {
        if (session) {
            writeSession(session);
        }
    }, [session]);

    /**
     * saveLoginResponse — called immediately after a successful /login API call.
     *
     * @param {object} apiResponse - Full JSON response from GlobalAuthController
     */
    const saveLoginResponse = (apiResponse) => {
        const {
            user,
            user_type,
            role,
            tenant,
            branches    = [],
            selected_branch,
        } = apiResponse;

        const isCompanyAdmin = user_type === 'company_admin';
        const isMultiBranch  = tenant?.max_branches > 1;

        // Company Admin with multi-branch → activeBranch is null until they pick one
        // Branch User → activeBranch is their fixed branch (selected_branch from server)
        const activeBranch = isCompanyAdmin ? null : (selected_branch ?? branches[0] ?? null);

        const newSession = {
            user,
            userType:     user_type,
            role,         // 'tenant' | 'super_admin'
            tenant,
            allBranches:  branches,
            activeBranch,
            isCompanyAdmin,
            isMultiBranch,
        };

        setSession(newSession);
        writeSession({ ...newSession, role }); // ensure role is persisted for ProtectedRoute
        return newSession;
    };

    /**
     * switchBranch — Company Admin only; updates the active branch.
     * Pass null to go back to "All Branches" aggregate view.
     *
     * @param {object|null} branch - branch object from allBranches, or null
     */
    const switchBranch = (branch) => {
        setSession(prev => ({ ...prev, activeBranch: branch }));
    };

    /** logout — wipe everything */
    const logout = () => {
        clearSession();
        setSession(null);
    };

    const value = {
        // State
        user:          session?.user          ?? null,
        userType:      session?.userType       ?? null,
        tenant:        session?.tenant         ?? null,
        allBranches:   session?.allBranches    ?? [],
        activeBranch:  session?.activeBranch   ?? null,
        isCompanyAdmin: session?.isCompanyAdmin ?? false,
        isMultiBranch:  session?.isMultiBranch  ?? false,
        isLoggedIn:    !!session?.user,

        // Methods
        saveLoginResponse,
        switchBranch,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
};

export default AuthContext;
